import { useState, useEffect, useRef } from "react";
import Sidebar from "./Sidebar";
import Topbar from "./Topbar";
import { Outlet, useLocation, Link } from "react-router-dom";
import { ArrowUp, Crosshair, MessageCircle, FileSignature, Plus, Calculator } from "lucide-react";
import { NavProvider } from "@/contexts/NavContext";
import { useAuth } from "@/contexts/AuthContext";

const TITLE_MAP = {
    "/dashboard": ["Dashboard Ejecutivo", "Vista 360° de la operación"],
    "/dispatch": ["Despacho Operativo", "Tablero central de cabina"],
    "/map": ["Mapa en Vivo", "Geolocalización de unidades"],
    "/services": ["Servicios", "Histórico y operativo"],
    "/services/new": ["Nueva Solicitud", "Captura de servicio"],
    "/quoter": ["Cotizador", "Genera cotizaciones inteligentes"],
    "/quotes": ["Cotizaciones", "Pipeline comercial"],
    "/clients": ["Clientes & Convenios", "CRM corporativo"],
    "/prospects": ["Prospectos (CRM)", "Leads y cotizaciones rápidas"],
    "/agreements": ["Tarifario por Convenio", "Reglas comerciales por cliente"],
    "/price-lists": ["Listas de Precios", "Catálogos tarifarios"],
    "/profitability": ["Rentabilidad", "Margen por cliente y tipo de servicio"],
    "/units": ["Unidades", "Flota de ambulancias"],
    "/personnel": ["Personal", "Expedientes y certificaciones"],
    "/payroll": ["Nómina del periodo", "Lo que se debe pagar a cada colaborador"],
    "/maintenance": ["Mantenimiento", "Preventivo y correctivo"],
    "/inventory": ["Inventario y Almacenes", "Catálogo + botiquines + movimientos"],
    "/receivable": ["Cuentas por Cobrar", "AR y antigüedad de saldos"],
    "/payable": ["Cuentas por Pagar", "AP y vencimientos"],
    "/incidents": ["Incidentes", "Siniestros y percances"],
    "/reports": ["Reportes", "Métricas operativas y financieras"],
    "/crew": ["Vista Móvil Tripulación", "Operación en campo"],
    "/audit": ["Auditoría", "Trazabilidad completa"],
    "/settings": ["Configuración", "Sistema y datos demo"],
    "/integrations": ["Integraciones", "Twilio, Resend, Ecofactura"],
    "/notifications": ["Notificaciones automáticas", "Plantillas WhatsApp / SMS / Email"],
    "/portal-users": ["Usuarios Portal", "Accesos para clientes corporativos"],
    "/users": ["Accesos del sistema", "Equipo, ambulancias y portal cliente"],
    "/sla": ["Cumplimiento SLA", "Tiempos de respuesta"],
    "/expirations": ["Centro de alertas", "Documentación próxima a vencer"],
    "/attendance": ["Asistencia y Turnos", "Geo-check-in"],
    "/aging": ["Antigüedad de Saldos", "AR + AP por rangos"],
    "/hospital-contacts": ["Contactos Hospitales", "Directorio de hospitales receptores"],
};

function LayoutShell() {
    const location = useLocation();
    const mainRef = useRef(null);
    const [showTop, setShowTop] = useState(false);
    const { user } = useAuth();
    const isAmbulancia = user?.role === "ambulancia";

    let path = location.pathname;
    if (path.startsWith("/services/") && path !== "/services/new") path = "/services";
    if (path.startsWith("/clients/")) path = "/clients";
    if (path.startsWith("/units/")) path = "/units";
    if (path.startsWith("/personnel/")) path = "/personnel";
    const [title, subtitle] = TITLE_MAP[path] || ["FindMed", "Dispatch Control"];

    // Botón "ir arriba"
    useEffect(() => {
        const el = mainRef.current;
        if (!el) return;
        const onScroll = () => setShowTop(el.scrollTop > 300);
        el.addEventListener("scroll", onScroll);
        return () => el.removeEventListener("scroll", onScroll);
    }, []);
    // Reset scroll al cambiar de ruta
    useEffect(() => {
        if (mainRef.current) mainRef.current.scrollTop = 0;
    }, [location.pathname]);

    const scrollTop = () => mainRef.current?.scrollTo({ top: 0, behavior: "smooth" });

    return (
        <div className="flex h-screen overflow-hidden bg-slate-100">
            {!isAmbulancia && <Sidebar />}
            <div className="flex-1 flex flex-col min-w-0">
                <Topbar title={title} subtitle={subtitle} />
                <main ref={mainRef} className="flex-1 overflow-auto bg-slate-100 fm-scroll" data-testid="main-content">
                    <div className="p-4 md:p-5 pb-24 md:pb-20 min-h-full">
                        <Outlet />
                    </div>
                </main>
                {/* Footer — sticky fuera del scroll, siempre visible al pie */}
                <footer className="border-t border-slate-200 bg-white px-5 py-2.5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 text-[10px] text-slate-500 z-30" data-testid="footer">
                    <div className="flex items-center gap-2">
                        <Crosshair className="w-3 h-3 text-red-600" />
                        <span className="font-bold tracking-widest uppercase text-slate-700">FindMed</span>
                        <span>· Dispatch Control v2.4</span>
                    </div>
                    {!isAmbulancia && (
                        <div className="flex items-center gap-4">
                            <Link to="/integrations" className="hover:text-red-600">Integraciones</Link>
                            <Link to="/settings" className="hover:text-red-600">Configuración</Link>
                            <Link to="/audit" className="hover:text-red-600">Auditoría</Link>
                            <a href="https://wa.me/" target="_blank" rel="noreferrer" className="hover:text-emerald-600 inline-flex items-center gap-1">
                                <MessageCircle className="w-3 h-3" /> Soporte
                            </a>
                        </div>
                    )}
                </footer>
                {/* Floating action button: quick new + back-to-top */}
                {showTop && (
                    <button
                        onClick={scrollTop}
                        className="fixed bottom-24 right-5 z-40 w-10 h-10 bg-slate-900 hover:bg-red-600 text-white rounded-full shadow-lg flex items-center justify-center transition-colors"
                        data-testid="back-to-top"
                        title="Volver arriba"
                    >
                        <ArrowUp className="w-4 h-4" />
                    </button>
                )}
                {!isAmbulancia && <QuickActions />}
            </div>
        </div>
    );
}

function QuickActions() {
    const [open, setOpen] = useState(false);
    return (
        <div className="fixed bottom-12 right-5 z-40 flex flex-col items-end gap-2">
            {open && (
                <div className="flex flex-col gap-2 mb-1" data-testid="quick-actions-menu">
                    <Link to="/services/new" onClick={() => setOpen(false)} className="bg-white border border-slate-200 hover:border-red-500 shadow-md rounded-sm px-3 py-2 text-xs font-bold uppercase tracking-wider flex items-center gap-2" data-testid="qa-new-service">
                        <FileSignature className="w-3.5 h-3.5 text-red-600" /> Nuevo servicio
                    </Link>
                    <Link to="/quoter" onClick={() => setOpen(false)} className="bg-white border border-slate-200 hover:border-red-500 shadow-md rounded-sm px-3 py-2 text-xs font-bold uppercase tracking-wider flex items-center gap-2" data-testid="qa-quoter">
                        <Calculator className="w-3.5 h-3.5 text-red-600" /> Cotizador
                    </Link>
                </div>
            )}
            <button
                onClick={() => setOpen(!open)}
                className={`w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-transform ${open ? "bg-red-700 rotate-45" : "bg-red-600 hover:bg-red-700"} text-white`}
                data-testid="quick-actions-fab"
                title="Acciones rápidas"
            >
                <Plus className="w-5 h-5" />
            </button>
        </div>
    );
}

export default function Layout() {
    return (
        <NavProvider>
            <LayoutShell />
        </NavProvider>
    );
}
